import { createRootRoute, createRoute, createRouter, Link, Outlet } from '@tanstack/react-router';
import { AppShell } from './components/AppShell';
import { AccessDeniedPage } from './pages/AccessDeniedPage';
import { AiAgentPage } from './pages/AiAgentPage';
import { AssetsPage } from './pages/AssetsPage';
import { AuditPage } from './pages/AuditPage';
import { CostPage } from './pages/CostPage';
import { CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { IncidentsPage } from './pages/IncidentsPage';
import { ImportExportPage } from './pages/ImportExportPage';
import { LoginPage } from './pages/LoginPage';
import { MapPage } from './pages/MapPage';
import { MonitoringPage } from './pages/MonitoringPage';
import { OAuthConsentPage } from './pages/OAuthConsentPage';
import { RoutesPage } from './pages/RoutesPage';
import { SettingsPage } from './pages/SettingsPage';
import { UsersPage } from './pages/UsersPage';

const rootRoute = createRootRoute({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (
    s: Record<string, unknown>,
  ): { returnTo?: string; error?: string; loggedOut?: boolean } => ({
    returnTo: typeof s.returnTo === 'string' ? s.returnTo : undefined,
    error: typeof s.error === 'string' ? s.error : undefined,
    loggedOut: s.loggedOut === 1 || s.loggedOut === '1' || s.loggedOut === true ? true : undefined,
  }),
  component: function Login() {
    const { returnTo, error, loggedOut } = loginRoute.useSearch();
    return <LoginPage returnTo={returnTo} error={error} loggedOut={loggedOut} />;
  },
});

const deniedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/akses-ditolak',
  component: AccessDeniedPage,
});

const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/consent',
  validateSearch: (s: Record<string, unknown>): { request?: string; error?: string } => ({
    request: typeof s.request === 'string' ? s.request : undefined,
    error: typeof s.error === 'string' ? s.error : undefined,
  }),
  component: function Consent() {
    const { request, error } = consentRoute.useSearch();
    return <OAuthConsentPage requestId={request} error={error} />;
  },
});

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppShell });

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage });

const mapRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/map',
  validateSearch: (s: Record<string, unknown>): { focus?: string; route?: string } => ({
    focus: typeof s.focus === 'string' ? s.focus : undefined,
    route: typeof s.route === 'string' ? s.route : undefined,
  }),
  component: function Map() {
    const { focus, route } = mapRoute.useSearch();
    return <MapPage key={`${focus ?? ''}:${route ?? ''}`} focus={focus} routeId={route} />;
  },
});

const routesRoute = createRoute({ getParentRoute: () => appRoute, path: '/routes', component: RoutesPage });

const monitoringRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/monitoring',
  component: MonitoringPage,
});

const transferRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/import-export',
  component: ImportExportPage,
});

const assetsRoute = createRoute({ getParentRoute: () => appRoute, path: '/assets', component: AssetsPage });

const usersRoute = createRoute({ getParentRoute: () => appRoute, path: '/users', component: UsersPage });

const auditRoute = createRoute({ getParentRoute: () => appRoute, path: '/audit', component: AuditPage });

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers',
  component: CustomersPage,
});

const alertsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/alerts',
  component: IncidentsPage,
});

const costRoute = createRoute({ getParentRoute: () => appRoute, path: '/cost', component: CostPage });

const aiAgentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ai-agent',
  component: AiAgentPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  deniedRoute,
  consentRoute,
  appRoute.addChildren([
    dashboardRoute,
    mapRoute,
    assetsRoute,
    routesRoute,
    monitoringRoute,
    transferRoute,
    usersRoute,
    auditRoute,
    settingsRoute,
    customersRoute,
    alertsRoute,
    costRoute,
    aiAgentRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => (
    <div className="grid h-full place-items-center p-6">
      <div className="fn-empty" style={{ maxWidth: 420 }}>
        <h1 className="fn-empty__title">Halaman tidak ditemukan</h1>
        <p className="fn-empty__text">Alamat ini tidak ada di FTTH NMS.</p>
        <Link to="/" className="fn-btn fn-btn--secondary">
          Ke Dashboard
        </Link>
      </div>
    </div>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
